<template>
  <div class="w-full">
    <!-- Tarih Seçici -->
    <div class="bg-gray-50 p-3 rounded-lg mb-4">
      <!-- Mod Seçimi -->
      <div class="flex gap-2 mb-3">
        <button
          @click="dateMode = 'single'"
          :class="dateMode === 'single' ? 'bg-red-500 text-white' : 'bg-white text-gray-700'"
          class="flex-1 px-2 py-1 text-xs rounded shadow-sm hover:bg-red-500 hover:text-white transition"
        >
          Tek Gün
        </button>
        <button
          @click="dateMode = 'range'"
          :class="dateMode === 'range' ? 'bg-red-500 text-white' : 'bg-white text-gray-700'"
          class="flex-1 px-2 py-1 text-xs rounded shadow-sm hover:bg-red-500 hover:text-white transition"
        >
          Tarih Aralığı
        </button>
      </div>

      <!-- Tek Gün Modu -->
      <template v-if="dateMode === 'single'">
        <div class="flex items-center justify-between mb-3">
          <button
            @click="changeDate(-1)"
            class="bg-white px-3 py-2 rounded shadow-sm hover:bg-gray-100 transition"
            :disabled="loading"
          >
            <i class="fas fa-chevron-left text-gray-600"></i>
          </button>

          <div class="text-center">
            <div class="text-sm font-semibold text-gray-700">
              {{ formattedDate }}
            </div>
            <div class="text-xs text-gray-500">
              {{ dateLabel }}
            </div>
          </div>

          <button
            @click="changeDate(1)"
            class="bg-white px-3 py-2 rounded shadow-sm hover:bg-gray-100 transition"
            :disabled="isToday || loading"
            :class="{ 'opacity-50 cursor-not-allowed': isToday }"
          >
            <i class="fas fa-chevron-right text-gray-600"></i>
          </button>
        </div>

        <!-- Hızlı Seçim Butonları -->
        <div class="flex gap-2">
          <button
            @click="selectToday"
            :class="isToday ? 'bg-red-500 text-white' : 'bg-white text-gray-700'"
            class="flex-1 px-2 py-1 text-xs rounded shadow-sm hover:bg-red-500 hover:text-white transition"
            :disabled="loading"
          >
            Bugün
          </button>
          <button
            @click="selectYesterday"
            :class="isYesterday ? 'bg-red-500 text-white' : 'bg-white text-gray-700'"
            class="flex-1 px-2 py-1 text-xs rounded shadow-sm hover:bg-red-500 hover:text-white transition"
            :disabled="loading"
          >
            Dün
          </button>
        </div>
      </template>

      <!-- Tarih Aralığı Modu -->
      <template v-else>
        <div class="space-y-2 mb-3">
          <div>
            <label class="text-xs text-gray-600 block mb-1">Başlangıç Tarihi ve Saati</label>
            <input
              type="datetime-local"
              v-model="startDateTimeInput"
              :max="endDateTimeInput || todayDateTimeStr"
              class="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>
          <div>
            <label class="text-xs text-gray-600 block mb-1">Bitiş Tarihi ve Saati</label>
            <input
              type="datetime-local"
              v-model="endDateTimeInput"
              :min="startDateTimeInput"
              :max="todayDateTimeStr"
              class="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-1 focus:ring-red-500"
            />
          </div>
        </div>

        <button
          @click="loadRangeLocations"
          :disabled="!startDateTimeInput || !endDateTimeInput || loading"
          class="w-full bg-red-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i class="fa fa-search mr-1"></i>
          Görüntüle
        </button>
      </template>
    </div>

    <!-- Yükleme Durumu -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-12">
      <i class="fa fa-spinner fa-spin text-3xl text-red-500 mb-3"></i>
      <p class="text-sm text-gray-500">Konum geçmişi yükleniyor...</p>
    </div>

    <!-- Hata Durumu -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-start">
        <i class="fa fa-exclamation-circle text-red-500 mt-1 mr-3"></i>
        <div class="flex-1">
          <p class="text-sm font-semibold text-red-800">Hata</p>
          <p class="text-xs text-red-600 mt-1">{{ error }}</p>

          <!-- Otomatik yeniden deneme sayacı -->
          <p v-if="retryCount > 0 && retryCount < maxRetries" class="text-xs text-gray-500 mt-2">
            <i class="fa fa-refresh fa-spin mr-1"></i>
            Otomatik yeniden deneniyor... ({{ retryCount }}/{{ maxRetries }})
          </p>

          <!-- Yeniden Dene Butonu -->
          <button
            v-else
            @click="retryLoad"
            class="mt-3 bg-red-500 text-white px-4 py-2 rounded text-xs font-semibold hover:bg-red-600 transition"
          >
            <i class="fa fa-refresh mr-1"></i>
            Yeniden Dene
          </button>
        </div>
      </div>
    </div>

    <!-- Veri Yok -->
    <div v-else-if="!locations || locations.length === 0" class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
      <i class="fa fa-map-marker text-4xl text-gray-300 mb-3"></i>
      <p class="text-sm font-semibold text-gray-600">Konum kaydı bulunamadı</p>
      <p class="text-xs text-gray-500 mt-1">
        Seçilen tarihte bu araç için konum kaydı mevcut değil
      </p>
    </div>

    <!-- Konum Listesi -->
    <div v-else>
      <!-- Özet Bilgiler -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <!-- Tarih Bilgisi -->
        <div class="text-xs font-semibold text-blue-800 mb-2">
          <i class="fa fa-calendar mr-2"></i>
          <span v-if="dateMode === 'single'">{{ formattedDate }}</span>
          <span v-else>{{ dateRangeLabel }}</span>
        </div>

        <!-- İstatistikler -->
        <div class="grid grid-cols-2 gap-2 text-xs">
          <!-- Konum Sayısı -->
          <div class="bg-blue-100 px-2 py-1 rounded">
            <i class="fa fa-map-marker text-blue-600 mr-1"></i>
            <span class="font-semibold text-blue-800">{{ locations.length }} kayıt</span>
          </div>

          <!-- Toplam Mesafe -->
          <div v-if="totalDistance > 0" class="bg-blue-100 px-2 py-1 rounded">
            <i class="fa fa-road text-blue-600 mr-1"></i>
            <span class="font-semibold text-blue-800">{{ totalDistance.toFixed(1) }} km</span>
          </div>

          <!-- Ortalama Hız -->
          <div v-if="averageSpeed > 0" class="bg-green-100 px-2 py-1 rounded col-span-2">
            <i class="fa fa-dashboard text-green-600 mr-1"></i>
            <span class="font-semibold text-green-800">Ort. Hız: {{ averageSpeed.toFixed(0) }} km/h</span>
          </div>
        </div>
      </div>

      <!-- Harita Üzerinde Göster Butonu -->
      <button
        @click="showOnMap"
        class="w-full bg-green-500 text-white py-2 rounded-lg mb-4 text-sm font-semibold hover:bg-green-600 transition"
      >
        <i class="fa fa-map mr-2"></i>
        Haritada Göster
      </button>

      <!-- Konum Kayıtları -->
      <div class="space-y-2 max-h-96 overflow-y-auto">
        <div
          v-for="(location, index) in limitedLocations"
          :key="index"
          class="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <!-- Saat -->
              <div class="text-xs font-semibold text-gray-700 mb-1">
                <i class="fa fa-clock-o text-gray-400 mr-1"></i>
                {{ formatTime(location.activityTime) }}
              </div>

              <!-- Konum -->
              <div class="text-xs text-gray-600 mb-2">
                <i class="fa fa-map-marker text-red-500 mr-1"></i>
                {{ formatAddress(location) }}
              </div>

              <!-- Detaylar -->
              <div class="space-y-1">
                <!-- Koordinatlar -->
                <div class="text-xs text-gray-500">
                  <i class="fa fa-location-arrow mr-1"></i>
                  {{ location.latitude.toFixed(5) }}, {{ location.longitude.toFixed(5) }}
                </div>

                <!-- Mesafe ve Hız (ilk nokta hariç) -->
                <div v-if="index > 0" class="flex items-center gap-3 text-xs">
                  <span class="text-blue-600 font-semibold">
                    <i class="fa fa-arrows-h mr-1"></i>
                    {{ getDistanceFromPrevious(index).toFixed(2) }} km
                  </span>
                  <span class="text-green-600 font-semibold">
                    <i class="fa fa-dashboard mr-1"></i>
                    ~{{ getEstimatedSpeed(index).toFixed(0) }} km/h
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Daha Fazla Göster -->
      <button
        v-if="locations.length > showCount"
        @click="showCount += 20"
        class="w-full mt-3 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition"
      >
        Daha Fazla Göster ({{ locations.length - showCount }} kaldı)
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import { tracking } from '@/networking/urlmanager';

export default {
  name: 'location-history',
  props: {
    detail: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dateMode: 'single', // 'single' veya 'range'
      selectedDate: moment(),
      startDateTimeInput: '',
      endDateTimeInput: '',
      locations: [],
      loading: false,
      error: null,
      showCount: 20,
      retryCount: 0,
      maxRetries: 3,
      retryDelay: 2000, // 2 saniye
      lastRequestType: null // 'single' veya 'range'
    };
  },
  computed: {
    formattedDate() {
      return this.selectedDate.format('DD MMMM YYYY');
    },
    dateLabel() {
      if (this.isToday) return 'Bugün';
      if (this.isYesterday) return 'Dün';
      return this.selectedDate.format('dddd');
    },
    isToday() {
      return this.selectedDate.isSame(moment(), 'day');
    },
    isYesterday() {
      return this.selectedDate.isSame(moment().subtract(1, 'day'), 'day');
    },
    limitedLocations() {
      return this.locations.slice(0, this.showCount);
    },
    totalDistance() {
      if (!this.locations || this.locations.length < 2) return 0;

      // Koordinatlardan toplam mesafeyi hesapla (Haversine formülü)
      let totalKm = 0;

      for (let i = 1; i < this.locations.length; i++) {
        const prev = this.locations[i - 1];
        const curr = this.locations[i];

        // Ardışık noktalar arasındaki mesafeyi hesapla
        const distance = this.calculateDistance(
          prev.latitude,
          prev.longitude,
          curr.latitude,
          curr.longitude
        );

        totalKm += distance;
      }

      return totalKm;
    },
    todayStr() {
      return moment().format('YYYY-MM-DD');
    },
    todayDateTimeStr() {
      return moment().format('YYYY-MM-DDTHH:mm');
    },
    dateRangeLabel() {
      if (!this.startDateTimeInput || !this.endDateTimeInput) return '';
      const start = moment(this.startDateTimeInput);
      const end = moment(this.endDateTimeInput);
      return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
    },
    averageSpeed() {
      if (!this.locations || this.locations.length < 2) return 0;

      const firstLocation = this.locations[0];
      const lastLocation = this.locations[this.locations.length - 1];

      // Toplam süreyi hesapla (saniye cinsinden)
      const totalSeconds = moment(lastLocation.activityTime).diff(
        moment(firstLocation.activityTime),
        'seconds'
      );

      // Çok kısa süre varsa veya sıfırsa
      if (totalSeconds <= 0) return 0;

      // Ortalama hız = Toplam mesafe / Toplam süre
      const totalHours = totalSeconds / 3600;
      const avgSpeed = this.totalDistance / totalHours;

      return avgSpeed;
    }
  },
  methods: {
    /**
     * İki koordinat arasındaki mesafeyi Haversine formülü ile hesaplar
     * @param {number} lat1 - Birinci nokta enlemi
     * @param {number} lon1 - Birinci nokta boylamı
     * @param {number} lat2 - İkinci nokta enlemi
     * @param {number} lon2 - İkinci nokta boylamı
     * @returns {number} - Mesafe (km)
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371; // Dünya'nın yarıçapı (km)
      const dLat = this.toRadians(lat2 - lat1);
      const dLon = this.toRadians(lon2 - lon1);

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRadians(lat1)) *
          Math.cos(this.toRadians(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      return distance;
    },

    /**
     * Dereceyi radyana çevirir
     */
    toRadians(degrees) {
      return degrees * (Math.PI / 180);
    },

    async loadLocations(isRetry = false) {
      if (!this.detail || !this.detail.plate) {
        this.error = 'Araç bilgisi eksik';
        return;
      }

      // İlk deneme ise retry sayacını sıfırla
      if (!isRetry) {
        this.retryCount = 0;
        this.lastRequestType = 'single';
      }

      this.loading = true;
      this.error = null;
      this.showCount = 20;

      try {
        const startTime = this.selectedDate.clone().startOf('day').format('YYYY-MM-DDTHH:mm:ss') + '+0000';
        const endTime = this.selectedDate.clone().endOf('day').format('YYYY-MM-DDTHH:mm:ss') + '+0000';

        const token = this.$store.state.userData.token;

        const response = await axios.get(
          tracking.getVehicleLocations,
          {
            params: {
              plate: this.detail.plate,
              muId: this.detail.muId,
              startTime,
              endTime
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.data && response.data.code === 200) {
          // Backend'den gelen data formatını normalize et ve zamana göre sırala
          this.locations = (response.data.data || [])
            .map(loc => ({
              activityTime: loc.time, // time -> activityTime
              latitude: loc.latitude,
              longitude: loc.longitude,
              city: loc.city || '',
              town: loc.town || '',
              cityCode: loc.cityCode,
              districtCode: loc.districtCode,
              eventLogId: loc.eventLogId,
              muId: loc.muId,
              networkId: loc.networkId,
              plate: loc.plate
            }))
            .sort((a, b) => moment(a.activityTime).diff(moment(b.activityTime))); // Eskiden yeniye sırala

          // Başarılı olduğunda retry sayacını sıfırla
          this.retryCount = 0;
        } else {
          throw new Error('Konum verileri alınamadı');
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Konum geçmişi yüklenirken bir hata oluştu';

        // Otomatik retry
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => {
            this.loadLocations(true);
          }, this.retryDelay);
        }
      } finally {
        this.loading = false;
      }
    },
    changeDate(days) {
      this.selectedDate = this.selectedDate.clone().add(days, 'days');
      this.loadLocations();
    },
    selectToday() {
      this.selectedDate = moment();
      this.loadLocations();
    },
    selectYesterday() {
      this.selectedDate = moment().subtract(1, 'day');
      this.loadLocations();
    },
    async loadRangeLocations(isRetry = false) {
      if (!this.detail || !this.detail.plate) {
        this.error = 'Araç bilgisi eksik';
        return;
      }

      if (!this.startDateTimeInput || !this.endDateTimeInput) {
        this.error = 'Başlangıç ve bitiş tarihi seçiniz';
        return;
      }

      // İlk deneme ise retry sayacını sıfırla
      if (!isRetry) {
        this.retryCount = 0;
        this.lastRequestType = 'range';
      }

      this.loading = true;
      this.error = null;
      this.showCount = 20;

      try {
        // datetime-local input'tan gelen format: YYYY-MM-DDTHH:mm
        const startTime = moment(this.startDateTimeInput).format('YYYY-MM-DDTHH:mm:ss') + '+0000';
        const endTime = moment(this.endDateTimeInput).format('YYYY-MM-DDTHH:mm:ss') + '+0000';

        const token = this.$store.state.userData.token;

        const response = await axios.get(
          tracking.getVehicleLocations,
          {
            params: {
              plate: this.detail.plate,
              muId: this.detail.muId,
              startTime,
              endTime
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.data && response.data.code === 200) {
          // Backend'den gelen data formatını normalize et ve zamana göre sırala
          this.locations = (response.data.data || [])
            .map(loc => ({
              activityTime: loc.time, // time -> activityTime
              latitude: loc.latitude,
              longitude: loc.longitude,
              city: loc.city || '',
              town: loc.town || '',
              cityCode: loc.cityCode,
              districtCode: loc.districtCode,
              eventLogId: loc.eventLogId,
              muId: loc.muId,
              networkId: loc.networkId,
              plate: loc.plate
            }))
            .sort((a, b) => moment(a.activityTime).diff(moment(b.activityTime))); // Eskiden yeniye sırala

          // Başarılı olduğunda retry sayacını sıfırla
          this.retryCount = 0;
        } else {
          throw new Error('Konum verileri alınamadı');
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Konum geçmişi yüklenirken bir hata oluştu';

        // Otomatik retry
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => {
            this.loadRangeLocations(true);
          }, this.retryDelay);
        }
      } finally {
        this.loading = false;
      }
    },
    /**
     * Manuel retry - Kullanıcı "Yeniden Dene" butonuna tıkladığında
     */
    retryLoad() {
      this.retryCount = 0;
      if (this.lastRequestType === 'range') {
        this.loadRangeLocations();
      } else {
        this.loadLocations();
      }
    },

    formatTime(dateTime) {
      return moment(dateTime).format('HH:mm:ss');
    },
    formatAddress(location) {
      const parts = [];
      // Backend'den sadece city ve town geliyor
      if (location.town) parts.push(location.town);
      if (location.city) parts.push(location.city);

      return parts.length > 0 ? parts.join(', ') : `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
    },

    /**
     * Bir önceki noktadan olan mesafeyi hesaplar
     */
    getDistanceFromPrevious(index) {
      if (index === 0 || !this.locations[index - 1]) return 0;

      const prev = this.locations[index - 1];
      const curr = this.locations[index];

      return this.calculateDistance(
        prev.latitude,
        prev.longitude,
        curr.latitude,
        curr.longitude
      );
    },

    /**
     * Tahmini hızı hesaplar (mesafe / zaman)
     */
    getEstimatedSpeed(index) {
      if (index === 0 || !this.locations[index - 1]) return 0;

      const prev = this.locations[index - 1];
      const curr = this.locations[index];

      // Mesafeyi hesapla (km)
      const distance = this.calculateDistance(
        prev.latitude,
        prev.longitude,
        curr.latitude,
        curr.longitude
      );

      // Zaman farkını hesapla (saat)
      const timeDiff = moment(curr.activityTime).diff(moment(prev.activityTime), 'seconds');

      // Çok kısa zaman farkı varsa veya sıfırsa hız hesaplanamaz
      if (timeDiff <= 0) return 0;

      // Hız = Mesafe / Zaman (km/h)
      const speedKmh = (distance / timeDiff) * 3600;

      // Mantıksız yüksek hızları filtrele (örn. 300 km/h üzeri)
      return speedKmh > 300 ? 0 : speedKmh;
    },
    showOnMap() {
      if (!this.locations || this.locations.length === 0) {
        return;
      }

      // Locations array'ini polyline formatına çevir ve unique noktaları filtrele
      const allPoints = this.locations.map(loc => ({
        lat: loc.latitude,
        lng: loc.longitude
      }));

      // Unique koordinatları filtrele (aynı noktayı tekrar ekleme)
      const polyline = [];
      let lastPoint = null;

      for (const point of allPoints) {
        if (!lastPoint ||
            Math.abs(point.lat - lastPoint.lat) > 0.00001 ||
            Math.abs(point.lng - lastPoint.lng) > 0.00001) {
          polyline.push(point);
          lastPoint = point;
        }
      }

      // Eğer sadece 1 unique nokta varsa (araç hareket etmemiş)
      if (polyline.length < 2) {
        alert('Bu tarihte araç hareket kayıtları bulunamadı. Araç aynı noktada durmuş görünüyor.');
        return;
      }

      // Mevcut görev sekmesi gibi selectedActiveDetail emit et
      // targetPoint'i boş gönder ki hedef marker'ı görünmesin
      this.$emit('selectedActiveDetail', {
        state: 1,
        targetPoint: { lat: 0, lng: 0 }, // Boş - marker görünmesin
        polyline: polyline,
        customer: `${this.selectedDate.format('DD MMMM YYYY')} Rota Geçmişi`,
      });
    }
  },
  watch: {
    detail: {
      immediate: true,
      handler() {
        if (this.detail && this.detail.plate) {
          this.loadLocations();
        }
      }
    }
  }
};
</script>

<style scoped>
/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
